/**
 * ============================================================================
 * USE WALLET HOOK
 * ============================================================================
 * 
 * Custom hook for wallet operations
 */

import { useState, useCallback } from 'react';
import * as walletService from '../services/wallet';

export const useWallet = () => {
  const [wallet, setWallet] = useState(null);
  const [balance, setBalance] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [withdrawalMethods, setWithdrawalMethods] = useState([]);

  /**
   * Get wallet (call explicitly from pages that need it; cached 30s, use options.invalidate to force refresh)
   */
  const loadWallet = useCallback(async (options = {}) => {
    if (options?.invalidate) walletService.clearWalletCache();
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWallet();
      // Backend returns { success: true, data: { wallet: {...} } }
      const walletData = response?.data?.wallet || response?.data || response;
      setWallet(walletData);
      setBalance(walletData?.availableBalance || walletData?.available_balance || 0);
      return walletData;
    } catch (err) {
      setError(err.message || 'Failed to load wallet');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get wallet balance
   */
  const loadBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getBalance();
      setBalance(response.data?.balance || 0);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load balance');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get wallet statistics
   */
  const loadStatistics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWalletStatistics();
      setStatistics(response.data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load statistics');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Request withdrawal
   */
  const requestWithdrawal = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.requestWithdrawal(data);
      await loadWallet({ invalidate: true }); // Refresh wallet with fresh data
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to request withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadWallet]);

  /**
   * Get withdrawal by ID
   */
  const getWithdrawal = useCallback(async (withdrawalId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWithdrawal(withdrawalId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * List withdrawals
   */
  const loadWithdrawals = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.listWithdrawals(filters);
      setWithdrawals(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load withdrawals');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Cancel withdrawal
   */
  const cancelWithdrawal = useCallback(async (withdrawalId, reason) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.cancelWithdrawal(withdrawalId, { reason });
      await loadWithdrawals(); // Refresh list
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to cancel withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadWithdrawals]);

  /**
   * Retry withdrawal
   */
  const retryWithdrawal = useCallback(async (withdrawalId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.retryWithdrawal(withdrawalId);
      await loadWithdrawals(); // Refresh list
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to retry withdrawal');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadWithdrawals]);

  /**
   * Get wallet transactions
   */
  const loadTransactions = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWalletTransactions(filters);
      const list = response?.data?.transactions ?? response?.transactions;
      setTransactions(Array.isArray(list) ? list : []);
      return response?.data ?? response;
    } catch (err) {
      setTransactions([]);
      setError(err.message || 'Failed to load transactions');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get wallet transaction by ID
   */
  const getWalletTransaction = useCallback(async (transactionId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWalletTransactionById(transactionId);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to get transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get wallet stats
   */
  const loadWalletStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWalletStats();
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load wallet stats');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Estimate withdrawal fees
   */
  const estimateWithdrawalFees = useCallback(async (data) => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.estimateWithdrawalFees(data);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to estimate fees');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get withdrawal methods
   */
  const loadWithdrawalMethods = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await walletService.getWithdrawalMethods();
      setWithdrawalMethods(response.data || []);
      return response.data;
    } catch (err) {
      setError(err.message || 'Failed to load withdrawal methods');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    wallet,
    balance,
    statistics,
    withdrawals,
    transactions,
    withdrawalMethods,
    loading,
    error,
    
    // Actions
    loadWallet,
    loadBalance,
    loadStatistics,
    requestWithdrawal,
    getWithdrawal,
    loadWithdrawals,
    cancelWithdrawal,
    retryWithdrawal,
    loadTransactions,
    getWalletTransaction,
    loadWalletStats,
    estimateWithdrawalFees,
    loadWithdrawalMethods,
    
    // Convenience
    refresh: loadWallet,
  };
};

export default useWallet;
